/**
 * useCharlotteChat — Text-only WebSocket chat with Charlotte.
 *
 * Opens a WebSocket to the configured Charlotte endpoint, sends text messages,
 * and receives transcript responses. Auto-reconnects on disconnect.
 */
import { useState, useRef, useCallback, useEffect } from 'react';

export interface ChatTranscript {
  id: string;
  source: 'user' | 'assistant';
  text: string;
  timestamp: number;
}

interface UseCharlotteChatOptions {
  /** WebSocket endpoint URL */
  endpoint: string;
  /** System prompt / instructions */
  systemPrompt?: string;
  /** RAG collection for context */
  ragCollection?: string;
  /** Bearer token or callback */
  auth?: string | (() => Promise<string>);
  /** Auto-connect on mount. Default: true */
  autoConnect?: boolean;
}

interface UseCharlotteChatReturn {
  transcripts: ChatTranscript[];
  connected: boolean;
  connecting: boolean;
  send: (text: string) => void;
  connect: () => void;
  disconnect: () => void;
  clearTranscripts: () => void;
}

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_BASE_MS = 1000;

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function useCharlotteChat(options: UseCharlotteChatOptions): UseCharlotteChatReturn {
  const { endpoint, systemPrompt, ragCollection, auth, autoConnect = true } = options;

  const [transcripts, setTranscripts] = useState<ChatTranscript[]>([]);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intentionalCloseRef = useRef(false);

  const doConnect = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setConnecting(true);
    intentionalCloseRef.current = false;

    try {
      // Build URL with query params
      const url = new URL(endpoint);
      if (systemPrompt) url.searchParams.set('instructions', systemPrompt);
      if (ragCollection) url.searchParams.set('rag_collection', ragCollection);

      if (auth) {
        const token = typeof auth === 'function' ? await auth() : auth;
        url.searchParams.set('token', token);
      }

      const ws = new WebSocket(url.toString());
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        setConnecting(false);
        reconnectCountRef.current = 0;
      };

      ws.onclose = () => {
        setConnected(false);
        setConnecting(false);
        wsRef.current = null;

        // Auto-reconnect if not intentional
        if (
          !intentionalCloseRef.current &&
          reconnectCountRef.current < MAX_RECONNECT_ATTEMPTS
        ) {
          const delay = RECONNECT_BASE_MS * Math.pow(2, reconnectCountRef.current);
          reconnectCountRef.current++;
          reconnectTimerRef.current = setTimeout(() => {
            doConnect();
          }, delay);
        }
      };

      ws.onerror = () => {
        // onclose will fire after onerror
      };

      ws.onmessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data as string) as {
            type: string;
            text?: string;
            source?: 'user' | 'assistant';
            message?: string;
          };

          switch (data.type) {
            case 'transcript_done': {
              const transcript: ChatTranscript = {
                id: generateId(),
                source: data.source ?? 'assistant',
                text: data.text ?? '',
                timestamp: Date.now(),
              };
              setTranscripts((prev) => [...prev, transcript]);
              break;
            }
            case 'text_response': {
              const transcript: ChatTranscript = {
                id: generateId(),
                source: 'assistant',
                text: data.text ?? data.message ?? '',
                timestamp: Date.now(),
              };
              setTranscripts((prev) => [...prev, transcript]);
              break;
            }
            case 'session_ready':
            case 'heartbeat':
            case 'pong':
              break;
            case 'error':
              // Could surface to UI, but for now just log
              break;
          }
        } catch {
          // Non-JSON message, ignore
        }
      };
    } catch {
      setConnecting(false);
    }
  }, [endpoint, systemPrompt, ragCollection, auth]);

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectCountRef.current = 0;
    wsRef.current?.close();
    wsRef.current = null;
    setConnected(false);
    setConnecting(false);
  }, []);

  const send = useCallback((text: string) => {
    if (!text.trim()) return;

    // Add user transcript immediately
    const userTranscript: ChatTranscript = {
      id: generateId(),
      source: 'user',
      text: text.trim(),
      timestamp: Date.now(),
    };
    setTranscripts((prev) => [...prev, userTranscript]);

    // Send via WebSocket
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'text',
        text: text.trim(),
      }));
    }
  }, []);

  const clearTranscripts = useCallback(() => {
    setTranscripts([]);
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect && endpoint) {
      doConnect();
    }
    return () => {
      disconnect();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    transcripts,
    connected,
    connecting,
    send,
    connect: doConnect,
    disconnect,
    clearTranscripts,
  };
}
